import { useEffect, useRef, useState } from 'react';

import type { CaseDetailDto, CaseEventDto } from '../../api/types';
import { fmtDate, fmtDateTime } from '../../lib/format';

/** Из `payload` показываем только человекочитаемый хвост события. */
function detailsOf(event: CaseEventDto): string {
    const payload = event.payload;
    if (!payload) return '';
    const keys = ['text', 'comment', 'reason', 'item_text', 'executor_name', 'department_name', 'category'];
    for (const key of keys) {
        const value = payload[key];
        if (typeof value === 'string' && value.trim().length > 0) return ': ' + value.trim();
    }
    if (typeof payload.justified === 'boolean') {
        return ': ' + (payload.justified ? 'признано обоснованным' : 'доводы не подтвердились');
    }
    return '';
}

export interface TabHistoryProps {
    detail: CaseDetailDto;
    currentUserId: string | null;
    busy: boolean;
    /** Счётчик нажатий кнопки «💬 Комментарий» — по нему ставим фокус в поле. */
    focusSeq: number;
    onComment: (text: string) => boolean;
}

/** Вкладка «История» (ТЗ §8.5.4): лента `case_events` и форма комментария. */
export function TabHistory({ detail, currentUserId, busy, focusSeq, onComment }: TabHistoryProps) {
    const [text, setText] = useState('');
    const inputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        if (focusSeq > 0 && inputRef.current) inputRef.current.focus();
    }, [focusSeq]);

    // Контракт отдаёт события от новых к старым; лента макета читается сверху вниз.
    const events = detail.events.slice().sort((a, b) => {
        const diff = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        return diff !== 0 ? diff : a.id - b.id;
    });

    const myId = currentUserId;

    function submit() {
        if (onComment(text)) setText('');
    }

    return (
        <>
            {events.length === 0 ? (
                <div className="detail-empty">По делу пока нет записей.</div>
            ) : (
                <div className="event-list">
                    {events.map((event) => (
                        <div
                            key={event.id}
                            className={'event-item' + (myId && event.actor_id === myId ? ' mine' : '')}
                        >
                            <div className="ev-time">{fmtDateTime(event.created_at)}</div>
                            <div>
                                <span className="ev-who">{event.actor_label || 'Система'}</span>
                                {' — '}
                                <span className="ev-what">{(event.label || event.type) + detailsOf(event)}</span>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {detail.permissions.comment ? (
                <div className="comment-box">
                    <input
                        ref={inputRef}
                        type="text"
                        value={text}
                        placeholder="Комментарий по делу"
                        aria-label="Комментарий по делу"
                        onChange={(event) => setText(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key !== 'Enter') return;
                            event.preventDefault();
                            submit();
                        }}
                    />
                    <button type="button" className="act-btn primary" disabled={busy} onClick={submit}>
                        Добавить
                    </button>
                </div>
            ) : null}

            {detail.case.closed_at ? (
                <div className="modal-note">
                    {'Дело закрыто ' + (fmtDate(detail.case.closed_at) || '—') + '. Переоткрыть дело может главный врач.'}
                </div>
            ) : null}
        </>
    );
}

export default TabHistory;
